import axios from 'axios';
import { API_URL } from '../constants/api';
import { FieldAnalytics, TopPlayersResponse } from '../types/analytics';
import { universalAuthService } from './universalAuthService';

// Создаем экземпляр axios для аналитики
const analyticsApiClient = axios.create({
  baseURL: API_URL,
});

// Настраиваем перехватчик для добавления токена в заголовки
analyticsApiClient.interceptors.request.use((config) => {
  // Пробуем получить токен поля или судьи
  const fieldToken = localStorage.getItem('fieldToken');
  const refereeToken = localStorage.getItem('refereeToken');

  const token = fieldToken || refereeToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor для автоматического обновления токена при 401
analyticsApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Определяем какой токен использовался
        const fieldToken = localStorage.getItem('fieldToken');
        const tokenType = fieldToken ? 'field' : 'referee';

        const newToken = await universalAuthService.refreshAccessToken(tokenType);

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return analyticsApiClient(originalRequest);
        }
      } catch (refreshError) {
        // Если обновление токена не удалось, перенаправляем на логин
        console.error('Token refresh failed:', refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const fieldAnalyticsService = {
  /**
   * Получить аналитику поля за период
   * @param fieldId - ID поля
   * @param dateFrom - Начало периода (опционально, по умолчанию начало текущего месяца)
   * @param dateTo - Конец периода (опционально, по умолчанию конец текущего месяца)
   * @param gameType - Тип игры (airsoft, paintball, speedball или undefined для всех)
   * @param tabs - Список вкладок для загрузки (overview, games, maps, referees, players, devices, operations, templates)
   */
  getFieldAnalytics: async (
    fieldId: string,
    dateFrom?: Date,
    dateTo?: Date,
    gameType?: string,
    tabs?: string[]
  ): Promise<FieldAnalytics> => {
    const params = new URLSearchParams();

    if (dateFrom) {
      params.append('dateFrom', dateFrom.toISOString());
    }
    if (dateTo) {
      params.append('dateTo', dateTo.toISOString());
    }
    if (gameType && gameType !== 'all') {
      params.append('gameType', gameType);
    }
    if (tabs && tabs.length > 0) {
      params.append('tabs', tabs.join(','));
    }

    const queryString = params.toString();
    const url = `/api/Field/${fieldId}/analytics${queryString ? `?${queryString}` : ''}`;

    const response = await analyticsApiClient.get<FieldAnalytics>(url);
    return response.data;
  },

  /**
   * Получить топ 100 игроков за период
   * @param fieldId - ID поля
   * @param dateFrom - Начало периода
   * @param dateTo - Конец периода
   * @param gameType - Тип игры (airsoft, paintball, speedball или undefined для всех)
   */
  getTopPlayers: async (
    fieldId: string,
    dateFrom: Date,
    dateTo: Date,
    gameType?: string
  ): Promise<TopPlayersResponse> => {
    const params = new URLSearchParams();
    params.append('dateFrom', dateFrom.toISOString());
    params.append('dateTo', dateTo.toISOString());

    if (gameType && gameType !== 'all') {
      params.append('gameType', gameType);
    }

    const url = `/api/Field/${fieldId}/analytics/top-players?${params.toString()}`;

    const response = await analyticsApiClient.get<TopPlayersResponse>(url);
    return response.data;
  },
};

export default fieldAnalyticsService;
